/**
 * Ethereum raw hex transaction deserializer.
 *
 * Parses a raw serialized Ethereum transaction hex string into
 * txViz's EthereumTransaction type using viem's parseTransaction.
 *
 * Accepts 0x-prefixed or bare hex of unsigned (or signed) RLP-encoded transactions:
 * - Legacy: RLP([nonce, gasPrice, gasLimit, to, value, data, ...])
 * - EIP-1559: 0x02 || RLP(...)
 * - EIP-2930: 0x01 || RLP(...)
 * - EIP-4844: 0x03 || RLP(...)
 */

import { parseTransaction } from 'viem';
import type { EthereumTransaction, EthereumAccessListEntry } from '@txviz/core';
import { hexToBytes, bytesToHex } from '@txviz/core';

/**
 * Parse a raw serialized Ethereum transaction.
 *
 * Accepts multiple encoding formats:
 * - 0x-prefixed hex (standard): `0x02f870...`
 * - Bare hex: `02f870...`
 * - Hex-encoded hex string (double-encoded): `3078303266383730...` → decodes to `0x02f870...`
 * - Base64-encoded RLP bytes: `Avhw...`
 *
 * @param input - The serialized transaction in any supported encoding
 * @returns A structured EthereumTransaction matching txViz's type system
 * @throws {Error} If the input cannot be parsed as a valid Ethereum transaction
 */
export function parseRawEthereumHex(input: string): EthereumTransaction {
  const decoded = decodeToHex(input.trim());

  // Unwrap RLP string envelope if present (Go's EncodeRLP wraps typed txs this way)
  // and validate the first byte for better error messages
  const normalized = unwrapRlpEnvelope(decoded);

  let parsed;
  try {
    parsed = parseTransaction(normalized as `0x${string}`);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Failed to parse Ethereum transaction: ${msg}\n\n` +
      `Expected formats:\n` +
      `  - EIP-1559: 0x02... (type 2 prefix + RLP-encoded fields)\n` +
      `  - EIP-2930: 0x01... (type 1 prefix + RLP-encoded fields)\n` +
      `  - Legacy:   0xc0... to 0xff... (RLP list prefix)\n` +
      `  - Base64:   base64-encoded RLP bytes\n\n` +
      `Common mistakes:\n` +
      `  - Pasting a tx hash instead of the serialized tx\n` +
      `  - Pasting calldata (function call) instead of the full transaction\n` +
      `  - Pasting a signature instead of the transaction`,
    );
  }

  // Base fields shared across all tx types
  const base = {
    chainId: BigInt(parsed.chainId ?? 1),
    nonce: BigInt(parsed.nonce ?? 0),
    to: (parsed.to ?? null) as string | null,
    value: parsed.value ?? 0n,
    data: (parsed.data ?? '0x') as string,
    gasLimit: parsed.gas ?? 0n,
  };

  const txType = parsed.type ?? 'legacy';

  switch (txType) {
    case 'eip1559': {
      const p = parsed as typeof parsed & {
        maxFeePerGas?: bigint;
        maxPriorityFeePerGas?: bigint;
        accessList?: readonly { address: string; storageKeys: readonly string[] }[];
      };
      return {
        ...base,
        type: 'eip1559' as const,
        maxFeePerGas: p.maxFeePerGas ?? 0n,
        maxPriorityFeePerGas: p.maxPriorityFeePerGas ?? 0n,
        accessList: mapAccessList(p.accessList),
      };
    }
    case 'eip2930': {
      const p = parsed as typeof parsed & {
        gasPrice?: bigint;
        accessList?: readonly { address: string; storageKeys: readonly string[] }[];
      };
      return {
        ...base,
        type: 'eip2930' as const,
        gasPrice: p.gasPrice ?? 0n,
        accessList: mapAccessList(p.accessList),
      };
    }
    case 'eip4844': {
      const p = parsed as typeof parsed & {
        maxFeePerGas?: bigint;
        maxPriorityFeePerGas?: bigint;
        maxFeePerBlobGas?: bigint;
        blobVersionedHashes?: readonly string[];
        accessList?: readonly { address: string; storageKeys: readonly string[] }[];
      };
      return {
        ...base,
        type: 'eip4844' as const,
        maxFeePerGas: p.maxFeePerGas ?? 0n,
        maxPriorityFeePerGas: p.maxPriorityFeePerGas ?? 0n,
        maxFeePerBlobGas: p.maxFeePerBlobGas ?? 0n,
        blobVersionedHashes: (p.blobVersionedHashes ?? []) as string[],
        accessList: mapAccessList(p.accessList),
      };
    }
    case 'legacy':
    default: {
      const p = parsed as typeof parsed & { gasPrice?: bigint };
      return {
        ...base,
        type: 'legacy' as const,
        gasPrice: p.gasPrice ?? 0n,
      };
    }
  }
}

function mapAccessList(
  list?: readonly { address: string; storageKeys: readonly string[] }[],
): EthereumAccessListEntry[] {
  if (!list) return [];
  return list.map((entry) => ({
    address: entry.address,
    storageKeys: [...entry.storageKeys],
  }));
}

// ── RLP envelope unwrapping & validation ──────────────────────

/**
 * Unwrap an RLP string envelope if present, then validate the first byte.
 *
 * Go's `tx.EncodeRLP()` wraps typed transactions (EIP-1559, etc.) in an
 * RLP string envelope: 0xb6 + inner_bytes (where 0xb6 - 0x80 = 54 = length).
 * We detect this pattern and strip the outer wrapper to get the raw typed tx.
 *
 * RLP string prefix ranges:
 *   0x80-0xb7: short string, length = firstByte - 0x80
 *   0xb8-0xbf: long string, length-of-length = firstByte - 0xb7
 *
 * After unwrapping, validates the first byte and gives actionable error messages.
 */
function unwrapRlpEnvelope(hex: string): string {
  if (hex.length < 4) {
    throw new Error('Input too short to be a valid Ethereum transaction.');
  }

  const firstByte = parseInt(hex.slice(2, 4), 16);

  // Already valid: typed tx envelope or RLP list
  if (
    (firstByte >= 0x01 && firstByte <= 0x04) ||
    firstByte >= 0xc0
  ) {
    return hex;
  }

  // Detect RLP string wrapping a typed transaction (Go's EncodeRLP pattern)
  if (firstByte >= 0x80 && firstByte <= 0xb7) {
    // Short RLP string: length = firstByte - 0x80
    const innerLen = firstByte - 0x80;
    const innerHex = hex.slice(4, 4 + innerLen * 2); // skip "0x" + 1 byte prefix
    if (innerHex.length === innerLen * 2) {
      const innerFirstByte = parseInt(innerHex.slice(0, 2), 16);
      // Check if inner data starts with a typed tx prefix (0x01-0x04)
      if (innerFirstByte >= 0x01 && innerFirstByte <= 0x04) {
        return `0x${innerHex}`;
      }
    }
  } else if (firstByte >= 0xb8 && firstByte < 0xc0) {
    // Long RLP string: length-of-length = firstByte - 0xb7
    const lenOfLen = firstByte - 0xb7;
    const lenHex = hex.slice(4, 4 + lenOfLen * 2);
    const innerLen = parseInt(lenHex, 16);
    const dataStart = 4 + lenOfLen * 2;
    const innerHex = hex.slice(dataStart, dataStart + innerLen * 2);
    if (innerHex.length === innerLen * 2 && innerHex.length >= 2) {
      const innerFirstByte = parseInt(innerHex.slice(0, 2), 16);
      if (innerFirstByte >= 0x01 && innerFirstByte <= 0x04) {
        return `0x${innerHex}`;
      }
    }
  }

  // Not a valid unwrap — give helpful error messages
  throwValidationError(hex, firstByte);
  return hex; // unreachable, but satisfies TS
}

/**
 * Throw a descriptive error for invalid first bytes.
 */
function throwValidationError(hex: string, firstByte: number): never {
  const cleanHex = hex.slice(2);

  // Looks like a 32-byte hash (64 hex chars)?
  if (cleanHex.length === 64) {
    throw new Error(
      'This looks like a transaction hash (32 bytes), not a serialized transaction.\n' +
      'To verify a transaction, you need the full serialized transaction data, not just its hash.\n' +
      'You can get the serialized tx from your wallet, RPC node (eth_getRawTransactionByHash), or block explorer.',
    );
  }

  // Looks like a signature (65 bytes = 130 hex chars)?
  if (cleanHex.length === 130) {
    throw new Error(
      'This looks like a signature (65 bytes: r + s + v), not a serialized transaction.\n' +
      'To verify a transaction, you need the full serialized transaction data before signing.',
    );
  }

  // First byte in 0x80-0xbf range = RLP string that didn't contain a typed tx
  if (firstByte >= 0x80 && firstByte < 0xc0) {
    throw new Error(
      `Invalid RLP structure: first byte 0x${firstByte.toString(16)} indicates an RLP byte string, not a transaction.\n` +
      'A valid Ethereum transaction starts with an RLP list (0xc0-0xff) or a type prefix (0x01-0x04).\n' +
      'You may have pasted calldata, an ABI-encoded value, or partial transaction data.\n' +
      'Make sure you paste the complete serialized transaction.',
    );
  }

  // First byte 0x05-0x7f: undefined transaction type
  if (firstByte >= 0x05 && firstByte <= 0x7f) {
    throw new Error(
      `Unknown transaction type: 0x${firstByte.toString(16)}.\n` +
      'Valid Ethereum transaction types are: legacy (RLP list), type 1 (EIP-2930), type 2 (EIP-1559), type 3 (EIP-4844), type 4 (EIP-7702).\n' +
      'This may not be a valid serialized Ethereum transaction.',
    );
  }

  // First byte 0x00
  throw new Error(
    'First byte is 0x00, which is not a valid transaction start.\n' +
    'This may be a null byte prefix, padding, or unrelated data.',
  );
}

// ── Encoding detection & normalization ─────────────────────────

/**
 * Smart decode: detects the encoding format and normalizes to 0x-prefixed hex.
 *
 * Detection logic:
 * 1. Already 0x-prefixed hex → use directly
 * 2. All hex chars → add 0x prefix
 * 3. Hex-encoded hex (double-encoded) → decode outer hex, check if result is a valid 0x hex string
 * 4. Base64-encoded RLP bytes → decode base64 to bytes, then hex-encode
 */
function decodeToHex(input: string): string {
  // Case 1: Already 0x-prefixed hex
  if (input.startsWith('0x') || input.startsWith('0X')) {
    return input.toLowerCase();
  }

  // Case 2: All hex chars, even length → probably bare hex
  if (/^[0-9a-fA-F]+$/.test(input) && input.length % 2 === 0) {
    // But first check: could this be hex-encoded "0x..." (double-encoded)?
    // "0x" in hex is "3078". If input starts with "3078", try decoding.
    if (input.toLowerCase().startsWith('3078')) {
      const decoded = hexToAscii(input);
      if (decoded && /^0x[0-9a-fA-F]+$/.test(decoded) && decoded.length % 2 === 0) {
        return decoded.toLowerCase();
      }
    }

    return `0x${input.toLowerCase()}`;
  }

  // Case 3: Might be base64-encoded RLP bytes
  if (/^[A-Za-z0-9+/]+=*$/.test(input)) {
    try {
      const binaryStr = atob(input);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      // Sanity check: first byte should be a valid RLP/type prefix
      const firstByte = bytes[0];
      if (
        firstByte !== undefined &&
        (firstByte >= 0xc0 || // RLP list (legacy tx)
          firstByte === 0x01 || // EIP-2930
          firstByte === 0x02 || // EIP-1559
          firstByte === 0x03 || // EIP-4844
          firstByte === 0x04)   // EIP-7702
      ) {
        return bytesToHex(bytes);
      }
    } catch {
      // Not valid base64, fall through
    }
  }

  // Fallback: assume it's hex without prefix
  return `0x${input.toLowerCase()}`;
}

/**
 * Decode a hex string to ASCII text.
 * Returns null if the hex doesn't decode to valid ASCII.
 */
function hexToAscii(hex: string): string | null {
  try {
    const bytes = hexToBytes(`0x${hex}`);
    let result = '';
    for (let i = 0; i < bytes.length; i++) {
      const byte = bytes[i]!;
      // Only accept printable ASCII + common whitespace
      if (byte < 0x09 || (byte > 0x0d && byte < 0x20) || byte > 0x7e) {
        return null;
      }
      result += String.fromCharCode(byte);
    }
    return result;
  } catch {
    return null;
  }
}
